import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { COMPANY } from "@/data/siteData";
import { useLanguage, LANGUAGES } from "@/i18n/LanguageContext";

function LangToggle({ compact = false }) {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="language-toggle" className={`flex items-center gap-0.5 border border-slate-300 ${compact ? "p-0.5" : "p-1"}`}>
      <Languages className="ml-1 h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-2 py-1 text-[11px] font-semibold transition-colors duration-150 ${
            lang === l.code ? "bg-navy text-gold" : "text-slate-500 hover:text-navy"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return null;

  const LINKS = [
    { to: "/", label: t("nav.home"), testid: "nav-link-home" },
    { to: "/commercial-insurance", label: t("nav.commercial"), testid: "nav-link-commercial" },
    { to: "/personal-insurance", label: t("nav.personal"), testid: "nav-link-personal" },
    { to: "/claims-support", label: t("nav.claims"), testid: "nav-link-claims" },
    { to: "/about-us", label: t("nav.about"), testid: "nav-link-about" },
    { to: "/insights", label: t("nav.insights"), testid: "nav-link-insights" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-link-contact" },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center" onClick={() => setOpen(false)}>
          <img src="/logo-dark.png" alt="Wisdom Insurance Broking" className="h-12 w-auto" />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 hover:text-amber-700 ${
                  isActive ? "text-amber-700" : "text-slate-600"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <LangToggle compact />
          <Link
            to="/contact"
            data-testid="nav-get-quote-btn"
            className="group flex items-center gap-1.5 bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-amber-700"
          >
            {t("nav.getQuote")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle compact />
          <button
            data-testid="nav-mobile-toggle"
            className="p-2 text-navy"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-slate-200 bg-white px-4 pb-6 pt-3 lg:hidden" aria-label="Mobile">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.testid}-mobile`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block border-b border-slate-100 py-3 text-base font-medium ${isActive ? "text-amber-700" : "text-slate-700"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-get-quote-btn-mobile"
            onClick={() => setOpen(false)}
            className="mt-4 flex items-center justify-center gap-2 bg-navy px-4 py-3 text-sm font-semibold text-white"
          >
            {t("nav.getQuote")} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </nav>
      )}
    </header>
  );
}
